import { createEffect } from 'effector'
import { check, LEVELS } from './level'

// save original console
globalThis.__console = globalThis.__console || globalThis.console

export class Logger implements Disposable {
  private allow: ReturnType<typeof check>

  constructor(
    public readonly prefix = '',
    public readonly writer = globalThis.__console,
    public readonly level?: number
  ) {
    this.allow = check(prefix, level)
  }

  new(prefix: string): Logger {
    return new Logger(this.prefix + '/' + prefix, this.writer)
  }

  prepend = (data: unknown[]): unknown[] => {
    const prefixStyle = 'color:gray;font-weight:normal;font-size:0.8em;'
    const defaultStyle = 'color:inherit;font-weight:inherit;font-size:inherit;'
    if (data.length > 0) {
      if (typeof data[0] === 'string' && data[0].includes('%')) {
        data[0] = `%c${this.prefix}%c ${data[0]}`
        data.splice(1, 0, prefixStyle, defaultStyle)
      } else {
        data.unshift(`%c${this.prefix}`, prefixStyle)
      }
    }
    return data
  }

  trace = (...data: unknown[]): void => {
    if (this.allow(LEVELS.trace)) {
      this.writer.trace(...this.prepend(data))
    }
  }

  debug = (...data: unknown[]): void => {
    if (this.allow(LEVELS.debug)) {
      this.writer.debug(...this.prepend(data))
    }
  }

  info = (...data: unknown[]): void => {
    if (this.allow(LEVELS.info)) {
      this.writer.info(...this.prepend(data))
    }
  }

  log = (...data: unknown[]): void => {
    if (this.allow(LEVELS.log)) {
      this.writer.log(...this.prepend(data))
    }
  }

  dir = (...data: unknown[]): void => {
    if (this.allow(LEVELS.log)) {
      this.writer.dir(...data) // `.dir` method doesn't support formatting
    }
  }

  warn = (...data: unknown[]): void => {
    if (this.allow(LEVELS.warn)) {
      this.writer.warn(...this.prepend(data))
    }
  }

  error = (...data: unknown[]): void => {
    if (this.allow(LEVELS.error)) {
      this.writer.error(...this.prepend(data))
    }
  }

  group = (...data: unknown[]): void => {
    this.writer.group(...this.prepend(data))
  }

  groupCollapsed = (...data: unknown[]): void => {
    this.writer.groupCollapsed(...this.prepend(data))
  }

  groupEnd = (): void => {
    this.writer.groupEnd()
  };

  [Symbol.dispose] = (): void => {
    this.groupEnd()
  }

  debugGroup = (...data: unknown[]): Logger | undefined => {
    if (this.allow(LEVELS.debug)) {
      this.groupCollapsed(...data)
      return new Logger('|', this.writer, LEVELS.trace)
    }
  }

  // effector extension
  traceFx = createEffect((data: unknown[]) => this.trace(...data))
  debugFx = createEffect((data: unknown[]) => this.debug(...data))
  infoFx = createEffect((data: unknown[]) => this.info(...data))
  logFx = createEffect((data: unknown[]) => this.log(...data))
  dirFx = createEffect((data: unknown[]) => this.dir(...data))
  warnFx = createEffect((data: unknown[]) => this.warn(...data))
  errorFx = createEffect((data: unknown[]) => this.error(...data))
}

// default application logger instance
export const logger = new Logger('tria')
